import { FunctionComponent } from 'react'

import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import { desktopCSS, mobileAndTabletCSS } from '@measures/responsive'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import useIsImmersiveHeaderActive from '@hooks/useIsImmersiveHeaderActive'
import useUser from '@hooks/useUser'
import Blink from '@components/Blink'
import SvgIcon from '@components/SvgIcon'

const {
  auth: { userProfilePath },
} = config

interface ImmersiveHeaderProps {
  isImmersiveHeaderActive: boolean
}

interface UserProfilePageProps {
  isUserProfilePage?: boolean
}

type UserInitialsProps = ImmersiveHeaderProps & UserProfilePageProps

const Wrapper = styled(Blink)`
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  width: 48px;
  height: 48px;

  ${desktopCSS(css`
    width: 40px;
    height: 40px;
  `)};
`

const UserLoginIconButton = styled(SvgIcon)<ImmersiveHeaderProps>`
  ${({
    theme: {
      colors: { staticWhite, iconBrand },
    },
    isImmersiveHeaderActive,
  }) => css`
    ${isImmersiveHeaderActive &&
    css`
      ${mobileAndTabletCSS(css`
        fill: ${staticWhite};
      `)}
    `}
    &:hover {
      fill: ${iconBrand};
    }
  `}
`

const StyledSvgIcon = styled(SvgIcon)<ImmersiveHeaderProps>`
  ${({
    theme: {
      colors: { staticWhite },
    },
    isImmersiveHeaderActive,
  }) => css`
    ${isImmersiveHeaderActive &&
    css`
      ${mobileAndTabletCSS(css`
        fill: ${staticWhite};
      `)}
    `}
  `}
`

const UserInitialsIcon = styled.div<UserInitialsProps>`
  ${({
    theme: {
      typography: {
        catchword: { small2: small2CatchwordCSS },
      },
      colors: { iconInverse, iconDefault, iconBrand, staticWhite, staticBlack },
    },
    isImmersiveHeaderActive,
    isUserProfilePage,
  }) => css`
    ${small2CatchwordCSS};

    width: 28px;
    height: 28px;
    color: ${iconInverse};
    background-color: ${isUserProfilePage ? iconBrand : iconDefault};
    border-radius: 50%;

    text-transform: uppercase;

    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: ${iconBrand};
    }

    ${isImmersiveHeaderActive &&
    css`
      ${mobileAndTabletCSS(css`
        color: ${staticBlack};
        background-color: ${staticWhite};
      `)}
    `}
  `}
`

const UserProfilePageLink: FunctionComponent = () => {
  const theme = useTheme()
  const isImmersiveHeaderActive = useIsImmersiveHeaderActive()
  const { isUserProfilePage } = useGetPageIdentity()
  const user = useUser()

  return (
    <Wrapper href={userProfilePath}>
      {user ? (
        !user.email_only && user.initials ? (
          <UserInitialsIcon
            isImmersiveHeaderActive={isImmersiveHeaderActive}
            isUserProfilePage={isUserProfilePage}
          >
            {user.initials.substring(0, 2)}
          </UserInitialsIcon>
        ) : (
          <StyledSvgIcon
            iconName="signed-in"
            size={24}
            color={theme.colors.iconBrand}
            isImmersiveHeaderActive={isImmersiveHeaderActive}
          />
        )
      ) : (
        <UserLoginIconButton
          iconName="sign-in"
          size={24}
          color={
            isUserProfilePage
              ? theme.colors.iconBrand
              : theme.colors.iconDefault
          }
          isImmersiveHeaderActive={isImmersiveHeaderActive}
        />
      )}
    </Wrapper>
  )
}

export default UserProfilePageLink
